import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button, ButtonSize, ButtonVariant, Spinner, SpinnerColor } from "../../ui";

/**
 * A refetch does not flip `loading`, and one that fails with the same error does not re-render, so the
 * button tracks its own attempt: disabled while it runs, and the screen re-renders when it ends.
 */
export const RetryButton: React.FC<{
  onRetry: () => Promise<unknown> | void;
  size?: ButtonSize.SMALL | ButtonSize.MEDIUM;
  // A full page gives it a wider tap target than the history does.
  minWidth?: number;
}> = ({ onRetry, size = ButtonSize.MEDIUM, minWidth }) => {
  const [retrying, setRetrying] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const retry = () => {
    setRetrying(true);
    Promise.resolve()
      .then(onRetry)
      .catch(() => undefined)
      .finally(() => mounted.current && setRetrying(false));
  };

  return (
    <Button
      size={size}
      variant={ButtonVariant.OUTLINED}
      disabled={retrying}
      onClick={retry}
      startIcon={retrying ? <Spinner size={16} color={SpinnerColor.INHERIT} aria-hidden /> : undefined}
      style={{
        textTransform: "none",
        fontWeight: 600,
        alignSelf: "center",
        minHeight: 44,
        ...(minWidth === undefined ? {} : { minWidth }),
      }}
    >
      {retrying ? "Trying…" : "Try again"}
    </Button>
  );
};
